import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';
import { Box } from '@mui/system';
import { FORM_ERROR } from 'final-form';
import { DataGrid } from '@mui/x-data-grid';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
// TODO: uncomment once api is available
// import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import { selectLoggedInUserEmail } from '@store/auth';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';
import {
  getRuleSources,
  addRuleSource,
  updateRuleSource,
  invalidateCache,
  selectRuleSources,
} from '../../store/orchestration-rules-sources.slice';
import ApplicationPageTemplate from '../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import TableToolbar from '../../../../components/TableToolbar';
import { viewRuleSources } from '../../payment-orchestration.breadcrumbs';

function Toolbar({
  setNewSourceDialogOpen,
  // TODO: uncomment once api is available
  // refreshCache,
  permissions,
}) {
  return (
    <TableToolbar>
      <div>
        {/* <Button
          // TODO: remove disabled attr once api is available
          // disabled={!permissions?.canRefreshPaymentOrchSourceCache}
          disabled
          variant="outlined"
          onClick={() => refreshCache()}
          startIcon={<ReplayCircleFilledIcon />}
          style={{ marginRight: 10 }}>
          Refresh Cache
        </Button> */}
        <Button
          variant="contained"
          disabled={!permissions?.canCreatePaymentOrchSource}
          onClick={() => setNewSourceDialogOpen(true)}
          startIcon={<AddIcon />}>
          New
        </Button>
      </div>
    </TableToolbar>
  );
}

export default function OrchestrationRuleSources({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sourceList = useSelector(selectRuleSources);
  const userEmail = useSelector(selectLoggedInUserEmail);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [newSourceDialogOpen, setNewSourceDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    if (!permissions?.canViewPaymentOrchSources) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewRuleSources));
  }, [dispatch]);

  const refreshCache = () => {
    dispatch(invalidateCache()).then(({ payload }) => {
      if (payload && payload.status === 'SUCCESS') {
        dispatch(showSuccessMessage('Successfully triggered refresh'));
      } else {
        dispatch(
          showErrorMessage({ message: payload.message, duration: 4000 }),
        );
      }
    });
  };

  const resetForm = useCallback(() => {
    setNewSourceDialogOpen(false);
    setFormValues({});
    setIsEditMode(false);
  }, []);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(getRuleSources()).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  const createNewSource = useCallback(
    (values) => {
      const { source, channelHash } = values;
      const payload = isEditMode
        ? {
            source,
            channelHash,
            user: userEmail,
          }
        : { ...values, user: userEmail };

      return dispatch(
        isEditMode
          ? updateRuleSource({
              payload,
              id: values.id,
            })
          : addRuleSource({
              payload,
            }),
      ).then(({ payload: resp }) => {
        if (resp && resp.data && resp.status === 'SUCCESS') {
          dispatch(
            showSuccessMessage(
              `Source ${isEditMode ? 'Updated' : 'Added'} Successfully`,
            ),
          );
          return resetForm();
        }

        return { [FORM_ERROR]: resp.message };
      });
    },
    [dispatch, isEditMode, resetForm, userEmail],
  );

  const validateNewSource = (values) => {
    const errors = {};
    if (!values.source) {
      errors.source = 'Source name is mandatory and cannot be empty';
    }
    if (!values.channelHash) {
      errors.channelHash = 'Channel hash is mandatory and cannot be empty';
    }
    return errors;
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title="Orchestration Rules Sources"
      description="Mapping of payment orchestration rules source and its channel hash"
      pageReady={!isFetchingData}>
      <DataGrid
        autoHeight
        density="standard"
        rows={sourceList}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableSelectionOnClick
        pageSize={10}
        rowsPerPageOptions={[5]}
        components={{
          Toolbar: () =>
            Toolbar({ setNewSourceDialogOpen, refreshCache, permissions }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        onRowClick={({ row }) => {
          setIsEditMode(true);
          setFormValues(row);
          setNewSourceDialogOpen(true);
        }}
        columns={[
          {
            field: 'id',
            headerName: 'ID',
            width: 100,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'source',
            headerName: 'Source',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            flex: 1,
          },
          {
            field: 'channelHash',
            headerName: 'Channel Hash',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            flex: 2,
          },
          {
            field: 'modifiedBy',
            headerName: 'Modified By',
            width: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'modifiedAt',
            headerName: 'Modified At',
            valueGetter: ({ row }) =>
              row.modifiedAt !== '' &&
              moment(row.modifiedAt).format('DD/MM/YYYY - HH:mm'),
            width: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
        ]}
      />
      <Dialog open={newSourceDialogOpen}>
        <DialogTitle>
          {isEditMode ? 'Update Existing Source' : 'Add New Source'}
        </DialogTitle>
        <Form
          onSubmit={createNewSource}
          initialValues={isEditMode ? formValues : {}}
          validate={validateNewSource}
          render={({ submitError, handleSubmit, values, submitting }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <DialogContent style={{ minHeight: '185px' }}>
                <Box sx={{ marginTop: '10px', minHeight: '80px' }}>
                  <TextField
                    label="Source name"
                    name="source"
                    required
                    variant="filled"
                    fullWidth
                    disabled={isEditMode}
                  />
                  <TextField
                    label="Channel Hash"
                    name="channelHash"
                    required
                    InputProps={{
                      readOnly: !permissions?.canUpdatePaymentOrchSource,
                    }}
                    variant="filled"
                    fullWidth
                    style={{ marginTop: 20 }}
                  />
                </Box>

                {submitError && (
                  <Alert
                    severity="error"
                    style={{ marginTop: 20 }}>
                    {submitError}
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  onClick={resetForm}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={
                    !permissions?.canCreatePaymentOrchSource ||
                    !permissions?.canUpdatePaymentOrchSource ||
                    submitting ||
                    (isEditMode &&
                      values.channelHash === formValues.channelHash)
                  }>
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </ApplicationPageTemplate>
  );
}
